import { p as proxyCustomElement, H, c as createEvent, h, F as Fragment } from './index.js';
import { i as isDefined } from './p-D_OOeJgV.js';
import { g as getValidityObject, c as checkValidity, r as reportValidity, s as setCustomValidity, a as setInputRef, b as getError, w as watchValue, d as watchError, e as watchWarning, f as renderFormField } from './p-Cg8AdZPK.js';
import { t as toValue, a as toLabel } from './p-CPuegN2k.js';
import { i as isLavOption } from './p-CdnDatdH.js';
import { i as isOtherOption } from './p-D36toVjT.js';
import { o as optionHasChildren, H as HiddenHTMLSelect } from './p-BYoISW3Y.js';
import { d as defineCustomElement$9 } from './p-BbTg3Car.js';
import { d as defineCustomElement$8 } from './p-DhNzpZvu.js';
import { d as defineCustomElement$7 } from './p-DgmKwGNG.js';
import { d as defineCustomElement$6 } from './p-BlW2joRF.js';
import { d as defineCustomElement$5 } from './p-Dr61d_nc.js';
import { d as defineCustomElement$4 } from './p-D-dZjG8L.js';
import { d as defineCustomElement$3 } from './p-DT9iU5If.js';
import { d as defineCustomElement$2 } from './p-_P4kPR3l.js';
import { d as defineCustomElement$1 } from './p-CdhcNtiX.js';

const checkInputGroupCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;display:flex;flex:none;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.inner{display:flex;gap:.75rem}span.header{color:var(--jw-theme-form-label-color);font-family:Open Sans;font-size:.875rem;font-weight:600;letter-spacing:-.0004em;line-height:1.25rem;margin-bottom:-.75rem;margin-top:-.75rem;padding-bottom:.25rem;padding-top:1rem}span.header:first-child{margin-top:0;padding-top:0}span.header:last-child{margin-bottom:0;padding-bottom:0}:host([orientation=horizontal]) .inner{flex-direction:row}:host([orientation=horizontal]) wui-check-input{flex:1}:host([orientation=vertical]) .inner{flex-direction:column}:host([orientation=vertical]) wui-check-input{width:100%}.with-children{display:flex;flex-direction:column;gap:.5rem}.expandable-option{align-items:center;display:flex;gap:.125rem}.expandable-option wui-check-input{flex:1}.children{display:flex;flex-direction:column;gap:.75rem;margin-left:3.5rem}.children[data-hidden]{display:none}";

function getChildrenCheckedStatus(option, value) {
    if (!optionHasChildren(option)) {
        return 'none';
    }
    const childValues = option.children.map(toValue);
    if (childValues.every((v) => value.includes(v))) {
        return 'every';
    }
    if (childValues.some((v) => value.includes(v))) {
        return 'some';
    }
    return 'none';
}
function getNewValue(currentValue, options, checked) {
    const optionValues = options
        .filter(isDefined)
        .filter((v) => isLavOption(v))
        .map(toValue);
    if (checked) {
        return [...currentValue, ...optionValues];
    }
    else {
        return currentValue.filter((v) => !optionValues.includes(v));
    }
}
function OptionComponent({ option, handleInput, disabled, value, size, name, }) {
    if (isOtherOption(option)) {
        return h("span", { class: "header" }, option.label);
    }
    const hasChildren = optionHasChildren(option);
    const optionLabel = hasChildren ? option.label : toLabel(option);
    const optionValue = hasChildren ? option.value : toValue(option);
    const optionDisabled = typeof option === 'object' ? option.disabled : undefined;
    const description = typeof option === 'object' && 'description' in option ? option.description : undefined;
    const childrenCheckedStatus = getChildrenCheckedStatus(option, value);
    const checkedProps = hasChildren
        ? {
            checked: childrenCheckedStatus === 'every',
            indeterminate: childrenCheckedStatus === 'some',
        }
        : {
            checked: (value || []).includes(toValue(option)),
            indeterminate: false,
        };
    return (h("wui-check-input", { ...(typeof option === 'object' ? option : {}), description: size === 'md' ? undefined : description, formField: size === 'md' && description ? { description } : undefined, ...checkedProps, disabled: disabled || optionDisabled, onChange: (e) => {
            // Prevent change event from bubbling up. We're using `input`.
            e.stopPropagation();
        }, onInput: (e) => {
            // Stop the inner input event from bubbling up
            e.stopPropagation();
            const target = e.currentTarget;
            if (disabled || optionDisabled || !target) {
                return;
            }
            handleInput(target.checked ?? false);
        }, name: name ? `${name}[]` : undefined, value: optionValue, label: optionLabel }));
}
const WuiCheckInputGroup = /*@__PURE__*/ proxyCustomElement(class WuiCheckInputGroup extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        //////// FORM FIELD START ////////
        this.value = [];
        this.required = false;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        //////// FORM FIELD END ////////
        /**
         * Options
         */
        this.options = [];
        /**
         * Inner WuiBox `orientation`
         */
        this.orientation = 'vertical';
        /**
         * If disabled
         */
        this.disabled = false;
        this.size = 'md';
        this.showChildren = {};
        this.setValue = (value = this.value) => {
            // Ensure values are unique
            this.value = Array.from(new Set(value));
            this.dirty = true;
            this.checkValidity();
            this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
        };
        this.toggleShowChildren = (option) => {
            const id = typeof option.value === 'string' ? option.value : JSON.stringify(option);
            // Create new object to trigger re-render
            this.showChildren = {
                ...this.showChildren,
                [id]: !this.showChildren[id],
            };
        };
        this.shouldHideChildren = (option) => {
            const id = typeof option.value === 'string' ? option.value : JSON.stringify(option);
            return !this.showChildren[id];
        };
    }
    _handleValueChange(newValue) {
        watchValue(this, newValue);
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    renderInner() {
        const { name, disabled, value, size } = this;
        const passThroughProps = { name, disabled, value, size };
        return (h(Fragment, null, h(HiddenHTMLSelect, { ref: this.setInputRef, value: value, options: this.options, required: !!this.required, disabled: disabled, multiple: true, name: name }), h("div", { class: "inner" }, this.options?.map((option) => {
            if (optionHasChildren(option)) {
                return (h("div", { class: "with-children" }, h("div", { class: "expandable-option" }, h("wui-button", { type: "icon", icon: this.shouldHideChildren(option) ? 'chevron-down' : 'chevron-up', onClick: (e) => {
                        e.stopPropagation();
                        this.toggleShowChildren(option);
                    } }), h(OptionComponent, { ...passThroughProps, option: option, handleInput: (checked) => {
                        this.setValue(getNewValue(this.value, [option, ...option.children], checked));
                    } })), h("div", { class: "children", "data-hidden": this.shouldHideChildren(option) }, option.children.map((child) => {
                    return (h(OptionComponent, { ...passThroughProps, value: this.value, option: child, handleInput: (checked) => {
                            const newValues = getNewValue(this.value, [option, child], checked);
                            // // Keep parent checked if some children are checked
                            // if (
                            //   getChildrenCheckedStatus(option, newValues) === 'some'
                            // ) {
                            //   newValues.push(toValue(option));
                            // }
                            this.setValue(newValues);
                        } }));
                }))));
            }
            return (h(OptionComponent, { ...passThroughProps, option: option, handleInput: (checked) => {
                    if (isOtherOption(option)) {
                        return;
                    }
                    this.setValue(getNewValue(this.value, [option], checked));
                } }));
        }))));
    }
    render() {
        return renderFormField(this, this.renderInner());
    }
    get elm() { return this; }
    static get watchers() { return {
        "value": ["_handleValueChange"],
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return checkInputGroupCss; }
}, [257, "wui-check-input-group", {
        "value": [1040],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16, "check-validity"],
        "reportValidity": [16, "report-validity"],
        "setCustomValidity": [16, "set-custom-validity"],
        "formField": [16, "form-field"],
        "options": [16],
        "orientation": [513],
        "disabled": [516],
        "name": [1],
        "size": [513],
        "customValidation": [32],
        "showValidity": [32],
        "showChildren": [32]
    }, undefined, {
        "value": ["_handleValueChange"],
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-check-input-group", "wui-box", "wui-button", "wui-check-input", "wui-form-field", "wui-icon", "wui-loader", "wui-status", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-check-input-group":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiCheckInputGroup);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiCheckInputGroup as W, defineCustomElement as d };
